// src/services/lubricentroService.ts - VERSIÓN ACTUALIZADA
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { Lubricentro, LubricentroStatus } from '../types';
import { TRIAL_LIMITS } from '../config/constants';

const COLLECTION_NAME = 'lubricentros';

// ========================================
// CONVERTIDORES DE DATOS
// ========================================

/**
 * Convertir datos de Firestore a nuestro tipo Lubricentro
 * Maneja conversión de timestamps y fechas automáticamente
 */
const convertFirestoreLubricentro = (doc: any) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    trialEndDate: data.trialEndDate?.toDate(),
    lastLogin: data.lastLogin?.toDate(),
    subscriptionStartDate: data.subscriptionStartDate?.toDate(),
    subscriptionEndDate: data.subscriptionEndDate?.toDate(),
    contractEndDate: data.contractEndDate?.toDate(),
    billingCycleEndDate: data.billingCycleEndDate?.toDate(),
    lastPaymentDate: data.lastPaymentDate?.toDate(),
    nextPaymentDate: data.nextPaymentDate?.toDate(),
    serviceSubscriptionExpiryDate: data.serviceSubscriptionExpiryDate?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    paymentHistory: data.paymentHistory?.map((payment: any) => ({
      ...payment,
      date: payment.date?.toDate() || new Date()
    })) || []
  } as Lubricentro;
};

// ========================================
// FUNCIÓN PRINCIPAL: CREAR LUBRICENTRO
// ========================================

/**
 * 🆕 FUNCIÓN ACTUALIZADA: Crea un nuevo lubricentro con TODOS los campos necesarios
 * 
 * Esta versión incluye:
 * ✅ Todos los campos básicos de información
 * ✅ Configuración inicial de período de prueba
 * ✅ Campos de suscripción preparados
 * ✅ Contadores inicializados en 0
 * ✅ Límites del período de prueba
 * ✅ Campos para planes por servicios
 * ✅ Fechas correctamente configuradas
 * 
 * @param data - Datos del lubricentro (sin id ni createdAt)
 * @param ownerId - ID del usuario propietario/admin
 * @returns ID del lubricentro creado
 */
export const createLubricentro = async (
  data: Omit<Lubricentro, 'id' | 'createdAt'>,
  ownerId: string
) => {
  try {
    // ========================================
    // 1. VALIDACIONES DE DATOS OBLIGATORIOS
    // ========================================
    if (!data.fantasyName?.trim()) throw new Error('Nombre del lubricentro es obligatorio');
    if (!data.responsable?.trim()) throw new Error('Responsable legal es obligatorio');
    if (!data.domicilio?.trim()) throw new Error('Domicilio es obligatorio');
    if (!data.cuit?.trim()) throw new Error('CUIT es obligatorio');
    if (!data.phone?.trim()) throw new Error('Teléfono es obligatorio');
    if (!data.email?.trim()) throw new Error('Email es obligatorio');
    if (!data.ticketPrefix?.trim()) throw new Error('Prefijo de ticket es obligatorio');
    if (!ownerId?.trim()) throw new Error('Owner ID es obligatorio');

    // ========================================
    // 2. CALCULAR FECHAS DEL PERÍODO DE PRUEBA
    // ========================================
    const now = new Date();
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + (TRIAL_LIMITS.DAYS || 7));

    // ========================================
    // 3. PREPARAR DATOS COMPLETOS PARA FIRESTORE
    // ========================================
    const lubricentroData = {
      // ----------------------------------------
      // INFORMACIÓN BÁSICA
      // ----------------------------------------
      fantasyName: String(data.fantasyName).trim(),
      responsable: String(data.responsable).trim(),
      domicilio: String(data.domicilio).trim(),
      cuit: String(data.cuit).trim().replace(/\D/g, ''), // Solo números
      phone: String(data.phone).trim(),
      email: String(data.email).trim().toLowerCase(),
      ticketPrefix: String(data.ticketPrefix).trim().toUpperCase(),

      // ----------------------------------------
      // PROPIETARIO Y ESTADO
      // ----------------------------------------
      ownerId: String(ownerId).trim(),
      estado: 'trial' as LubricentroStatus, // Estado inicial: período de prueba

      // ----------------------------------------
      // FECHAS PRINCIPALES
      // ----------------------------------------
      trialEndDate: trialEndDate, // Fin del período de prueba
      createdAt: serverTimestamp(), // Fecha de creación (se sincroniza con Firebase)

      // ----------------------------------------
      // CONTADORES DE SERVICIOS (MENSUAL)
      // ----------------------------------------
      servicesUsedThisMonth: 0, // Servicios usados en el mes actual
      activeUserCount: 1, // Por defecto 1 (el admin/owner)
      servicesUsed: 0, // Servicios ya utilizados

      // ----------------------------------------
      // LÍMITES Y CONFIGURACIÓN DE PLAN
      // ----------------------------------------
      maxUsersAllowed: TRIAL_LIMITS.USERS || 1, // Límite de usuarios durante prueba
      maxMonthlyServices: TRIAL_LIMITS.SERVICES || 25, // Límite de servicios durante prueba

      // ----------------------------------------
      // HISTORIAL Y TRACKING
      // ----------------------------------------
      servicesUsedHistory: {}, // Historial de uso por mes (formato: 'YYYY-MM': cantidad)
      paymentHistory: [] as any[], // Historial de pagos vacío
      renewalCount: 0, // Contador de renovaciones
      autoRenewal: false, // Renovación automática desactivada por defecto
      hasUnlimitedServices: false,

      // ----------------------------------------
      // CONFIGURACIÓN DE SUSCRIPCIÓN (TRIAL)
      // ----------------------------------------
      subscriptionRenewalType: 'service' as const, // Tipo: Por Servicios
      totalServicesContracted: TRIAL_LIMITS.SERVICES || 10, // Servicios iniciales de prueba
      servicesRemaining: TRIAL_LIMITS.SERVICES || 10, // Servicios disponibles inicialmente
      subscriptionStartDate: now, // Inicio de la relación
      serviceSubscriptionExpiryDate: trialEndDate, // Vencimiento coincidente con trial

      // ----------------------------------------
      // UBICACIÓN (OPCIONAL)
      // ----------------------------------------
      location: data.location || {
        lat: undefined,
        lng: undefined,
        address: undefined
      },

      // ----------------------------------------
      // CAMPOS OPCIONALES (solo si existen)
      // ----------------------------------------
      ...(data.logoUrl && { logoUrl: data.logoUrl }),
      ...(data.logoBase64 && { logoBase64: data.logoBase64 })
    };

    // ========================================
    // 4. CREAR EL DOCUMENTO EN FIRESTORE
    // ========================================
    const docRef = await addDoc(collection(db, COLLECTION_NAME), lubricentroData);

    // ========================================
    // 5. NOTIFICAR AL SUPERADMIN (Background)
    // ========================================
    try {
      const { notifyNewLubricentro } = await import('./notificationService');
      notifyNewLubricentro({
        id: docRef.id,
        ...lubricentroData,
        trialEndDate: trialEndDate.toLocaleDateString()
      }).catch(err => console.warn('Error enviando notificación:', err));
    } catch (err) {
      console.warn('Error importando notificationService:', err);
    }

    return docRef.id;

  } catch (error) {
    console.error('❌ Error al crear lubricentro:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: ACTUALIZAR LUBRICENTRO
// ========================================

/**
 * Actualiza un lubricentro existente
 * Solo actualiza los campos que no son undefined
 */
export const updateLubricentro = async (id: string, data: Partial<Lubricentro>) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);

    // Crear objeto sin campos undefined
    const updateData: any = {
      updatedAt: serverTimestamp()
    };

    // Solo agregar campos que no sean undefined
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (value !== undefined) {
        updateData[key] = value;
      }
    });

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('❌ Error al actualizar el lubricentro:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: OBTENER LUBRICENTRO POR ID
// ========================================

/**
 * Obtiene un lubricentro por su ID
 */
export const getLubricentroById = async (id: string) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return convertFirestoreLubricentro(docSnap);
    }

    return null;
  } catch (error) {
    console.error('❌ Error al obtener lubricentro:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: OBTENER TODOS LOS LUBRICENTROS
// ========================================

/**
 * Obtiene todos los lubricentros
 */
export const getAllLubricentros = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertFirestoreLubricentro(doc));
  } catch (error) {
    console.error('❌ Error al obtener lubricentros:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: OBTENER LUBRICENTROS ACTIVOS
// ========================================

/**
 * Obtiene lubricentros activos (para registro de empleados)
 */
export const getActiveLubricentros = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('estado', 'in', ['activo', 'trial']),
      orderBy('fantasyName', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertFirestoreLubricentro(doc));
  } catch (error) {
    console.error('❌ Error al obtener lubricentros activos:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: OBTENER LUBRICENTROS POR DUEÑO
// ========================================

/**
 * Obtiene lubricentros por owner ID
 */
export const getLubricentrosByOwner = async (ownerId: string) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('ownerId', '==', ownerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertFirestoreLubricentro(doc));
  } catch (error) {
    console.error('❌ Error al obtener lubricentros por owner:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: ELIMINAR LUBRICENTRO
// ========================================

/**
 * Elimina un lubricentro
 */
export const deleteLubricentro = async (id: string) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('❌ Error al eliminar lubricentro:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: SUBIR LOGO
// ========================================

/**
 * Sube el logo del lubricentro a Firebase Storage
 */
export const uploadLogo = async (
  lubricentroId: string,
  file: File
) => {
  try {
    const storageRef = ref(storage, `lubricentros/${lubricentroId}/logo-${Date.now()}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Actualizar el lubricentro con la URL del logo
    await updateLubricentro(lubricentroId, { logoUrl: downloadURL });

    return downloadURL;
  } catch (error) {
    console.error('❌ Error al subir logo:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: ELIMINAR LOGO
// ========================================

/**
 * Elimina el logo del lubricentro de Firebase Storage
 */
export const deleteLogo = async (lubricentroId: string, logoUrl: string) => {
  try {
    const storageRef = ref(storage, logoUrl);
    await deleteObject(storageRef);

    // Actualizar el lubricentro para quitar la URL del logo
    await updateLubricentro(lubricentroId, { logoUrl: undefined });
  } catch (error) {
    console.error('❌ Error al eliminar logo:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: ACTUALIZAR ESTADO
// ========================================

/**
 * Actualiza el estado de un lubricentro
 */
export const updateLubricentroStatus = async (
  id: string,
  estado: LubricentroStatus
) => {
  try {
    await updateLubricentro(id, { estado });
  } catch (error) {
    console.error('❌ Error al actualizar estado:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: VERIFICAR PREFIJO ÚNICO
// ========================================

/**
 * Verifica si un prefijo de ticket ya existe
 */
export const isPrefixUnique = async (prefix: string) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('ticketPrefix', '==', prefix.toUpperCase()),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  } catch (error) {
    console.error('❌ Error al verificar prefijo:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: EXTENDER PERÍODO DE PRUEBA
// ========================================

/**
 * Extiende el período de prueba de un lubricentro
 * @param lubricentroId - ID del lubricentro
 * @param additionalDays - Días adicionales a agregar
 */
export const extendTrialPeriod = async (
  lubricentroId: string,
  additionalDays: number = 7
): Promise<void> => {
  try {
    const lubricentro = await getLubricentroById(lubricentroId);

    if (!lubricentro) {
      throw new Error('Lubricentro no encontrado');
    }

    if (lubricentro.estado !== 'trial') {
      throw new Error('Solo se puede extender el período de prueba para lubricentros en trial');
    }

    // Calcular nueva fecha de fin de prueba
    const currentTrialEnd = lubricentro.trialEndDate || new Date();
    const newTrialEnd = new Date(currentTrialEnd);
    newTrialEnd.setDate(newTrialEnd.getDate() + additionalDays);

    await updateLubricentro(lubricentroId, {
      trialEndDate: newTrialEnd
    });
  } catch (error) {
    console.error('❌ Error al extender período de prueba:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: OBTENER LUBRICENTRO POR SUBSCRIPTION ID
// ========================================

/**
 * Obtiene un lubricentro por su subscriptionId (para Mercado Pago)
 */
export const getLubricentroBySubscriptionId = async (
  subscriptionId: string
): Promise<Lubricentro | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('subscriptionId', '==', subscriptionId),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    return convertFirestoreLubricentro(querySnapshot.docs[0]);
  } catch (error) {
    console.error('❌ Error al obtener lubricentro por subscriptionId:', error);
    throw error;
  }
};

// ========================================
// FUNCIÓN: ALIAS PARA COMPATIBILIDAD
// ========================================

/**
 * Alias de uploadLogo para compatibilidad con código existente
 */
export const uploadLubricentroLogo = uploadLogo;

// ========================================
// EXPORTACIONES
// ========================================

export default {
  createLubricentro,
  updateLubricentro,
  getLubricentroById,
  getAllLubricentros,
  getActiveLubricentros,
  getLubricentrosByOwner,
  deleteLubricentro,
  uploadLogo,
  uploadLubricentroLogo, // Alias para compatibilidad
  deleteLogo,
  updateLubricentroStatus,
  isPrefixUnique,
  extendTrialPeriod,
  getLubricentroBySubscriptionId
};

